import { FC, useEffect, useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import style from './CoatingStep2Style.module.css';
import { PhoneInput } from './PhoneInput';
import { Button } from '@/components/uiParts/Button';
import { DateTimeSelect } from '@/components/uiParts/DateTimeSelect/DateTimeSelect';
import { COATING_MIN_DAYS_AHEAD } from '@/consts/coating';
import {
  CoatingStep2FormData,
  useCoatingStep2,
} from '@/hooks/api/coating/useCoatingStep2';
import { CoatingUnitItem } from '@/types/api/coatingApi';
import { generateCoatingTimeSlots } from '@/utils/coatingTimeSlots';

interface Props {
  step2Data: CoatingStep2FormData;
  coatingUnits: CoatingUnitItem[];
  workingMinutes: number;
  customerEmail?: string;
  onSubmit: (data: CoatingStep2FormData) => void;
  onClickCancel: () => void;
}

const formatWorkingMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}分`;
  if (mins === 0) return `${hours}時間`;
  return `${hours}時間${mins}分`;
};

export const CoatingStep2: FC<Props> = ({
  step2Data,
  coatingUnits,
  workingMinutes,
  customerEmail,
  onSubmit,
  onClickCancel,
}) => {
  const { formMethods } = useCoatingStep2(step2Data);
  const {
    watch,
    register,
    trigger,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
  } = formMethods;
  const datetime1 = watch('preferredDatetimes.datetime1');
  const datetime2 = watch('preferredDatetimes.datetime2');
  const mUnitId = watch('mUnitId');
  const prevUnitIdRef = useRef(mUnitId);

  const selectedUnit = useMemo(
    () => coatingUnits.find((u) => u.m_unit_id === Number(mUnitId)),
    [coatingUnits, mUnitId],
  );

  const timeSlotOptions = useMemo(
    () =>
      generateCoatingTimeSlots(
        selectedUnit?.start_hm,
        selectedUnit?.end_hm,
        workingMinutes,
      ),
    [selectedUnit, workingMinutes],
  );

  // 店舗変更時に希望日時をクリアしてDateTimeSelectを再生成させる
  useEffect(() => {
    if (prevUnitIdRef.current !== mUnitId) {
      setValue('preferredDatetimes.datetime1', '');
      setValue('preferredDatetimes.datetime2', '');
      setValue('preferredDatetimes.datetime3', '');
      prevUnitIdRef.current = mUnitId;
    }
  }, [mUnitId]);

  useEffect(() => {
    trigger('reservationHolder');
  }, []);

  // 顧客マスタにメールがあれば初期値として設定（未入力時のみ）
  useEffect(() => {
    if (customerEmail && !watch('contactEmail')) {
      setValue('contactEmail', customerEmail, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={style['container']}>
          {/* 施工店舗 */}
          <div className={style['input-group']}>
            <h2 className={style['input-label']}>
              施工店舗
              <span
                className={`${style['input-label-tips']} ${style['required']}`}
              >
                必須
              </span>
            </h2>
            <select
              className={`${style['unit-select']} ${
                errors.mUnitId ? style['unit-select-error'] : ''
              }`}
              {...register('mUnitId', { valueAsNumber: true })}
              defaultValue=""
            >
              <option value="" disabled>
                店舗を選択してください
              </option>
              {coatingUnits.map((unit) => (
                <option key={unit.m_unit_id} value={unit.m_unit_id}>
                  {unit.unit_name}
                </option>
              ))}
            </select>
          </div>

          {/* 希望日時 */}
          <div className={`${style['input-group']} ${style['has-border-top']}`}>
            <h2 className={style['input-label']}>
              ご予約のご希望のお日にち・お時間を選択してください
            </h2>
            {workingMinutes > 0 && (
              <div className={style['info-box']}>
                <h4 className={style['info-box-title']}>所要時間の目安</h4>
                <ul className={style['info-box-list']}>
                  <li>約 {formatWorkingMinutes(workingMinutes)}</li>
                </ul>
              </div>
            )}
            <DateTimeSelect
              key={`datetime1-${mUnitId || 'none'}`}
              label="第一希望"
              name="preferredDatetimes.datetime1"
              showError={!!errors.preferredDatetimes?.datetime1 && !!datetime1}
              required
              minDaysAhead={COATING_MIN_DAYS_AHEAD}
              timeOptions={timeSlotOptions}
            />
            <DateTimeSelect
              key={`datetime2-${mUnitId || 'none'}`}
              label="第二希望"
              name="preferredDatetimes.datetime2"
              showError={
                errors.preferredDatetimes?.datetime2 &&
                (!errors.preferredDatetimes?.datetime1 || !datetime1)
              }
              required
              minDaysAhead={COATING_MIN_DAYS_AHEAD}
              timeOptions={timeSlotOptions}
            />
            <DateTimeSelect
              key={`datetime3-${mUnitId || 'none'}`}
              label="第三希望"
              name="preferredDatetimes.datetime3"
              required
              minDaysAhead={COATING_MIN_DAYS_AHEAD}
              timeOptions={timeSlotOptions}
            />
          </div>

          {/* 予約者情報 */}
          <div className={`${style['input-group']} ${style['has-border-top']}`}>
            <h2 className={style['input-label']}>
              当日対応いただけるお客さまの情報を入力してください。
            </h2>
            <div className={style['input-group']}>
              <div
                className={`${style['input-text']} ${
                  !!errors.reservationHolder?.familyName ||
                  !!errors.reservationHolder?.firstName
                    ? style['input-text-error']
                    : ''
                }`}
              >
                <label>
                  お名前
                  <span
                    className={`${style['input-label-tips']} ${style['required']}`}
                  >
                    必須
                  </span>
                </label>
                <div className={style['input-group-1']}>
                  <input
                    placeholder="姓"
                    {...register('reservationHolder.familyName')}
                  />
                  <input
                    placeholder="名"
                    {...register('reservationHolder.firstName')}
                  />
                </div>
              </div>
            </div>
            <div className={style['input-group']}>
              <div
                className={`${style['input-text']} ${
                  !!errors.reservationHolder?.familyNameKana ||
                  !!errors.reservationHolder?.firstNameKana
                    ? style['input-text-error']
                    : ''
                }`}
              >
                <label>
                  フリガナ
                  <span
                    className={`${style['input-label-tips']} ${style['required']}`}
                  >
                    必須
                  </span>
                </label>
                <div className={style['input-group-1']}>
                  <input
                    placeholder="セイ"
                    {...register('reservationHolder.familyNameKana')}
                  />
                  <input
                    placeholder="メイ"
                    {...register('reservationHolder.firstNameKana')}
                  />
                </div>
              </div>
            </div>
            <PhoneInput />
          </div>

          {/* 連絡方法選択 */}
          <div className={`${style['input-group']} ${style['has-border-top']}`}>
            <h2 className={style['input-label']}>
              ご連絡を受けたい方法
              <span
                className={`${style['input-label-tips']} ${style['required']}`}
              >
                必須
              </span>
            </h2>
            <p className={style['input-label-sub']}>
              ご予約内容の確認や日程のご相談に使用します。
            </p>
            <label className={style['radio-row']}>
              <input
                type="radio"
                value={1}
                {...register('contactMethod', { valueAsNumber: true })}
              />
              <span>お電話</span>
            </label>
            <label className={style['radio-row']}>
              <input
                type="radio"
                value={2}
                {...register('contactMethod', { valueAsNumber: true })}
              />
              <span>メール</span>
            </label>

            <div className={style['input-text']} style={{ marginTop: '8px' }}>
              <label>
                メールアドレス
                <span
                  className={`${style['input-label-tips']} ${style['required']}`}
                >
                  必須
                </span>
              </label>
              <p className={style['input-label-sub']}>
                ご予約完了のお知らせをお送りします。
              </p>
              <input
                type="email"
                inputMode="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className={style['input-input']}
                placeholder="example@example.com"
                {...register('contactEmail')}
              />
              {!!errors.contactEmail && (
                <p className={style['error-text']}>
                  {errors.contactEmail.message as string}
                </p>
              )}
            </div>
          </div>

          {/* 備考 */}
          <div className={`${style['input-group']} ${style['has-border-top']}`}>
            <div className={style['input-text']}>
              <label>
                備考
                <span className={style['input-label-tips']}>任意</span>
              </label>
              <textarea
                className={style['input-textarea']}
                rows={5}
                placeholder="ご要望やお車に関する補足事項などがあればご記入ください。"
                {...register('unitMemo')}
              />
            </div>
          </div>

          <div className={style['button-group']}>
            <Button text="次へ" type="submit" disabled={!isValid} />
            <Button
              style={{
                color: '#232323',
                backgroundColor: '#fff',
                marginTop: '8px',
              }}
              text="キャンセル"
              onClick={onClickCancel}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
